import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { API_BASE_URL } from '../config';
import { isAuthenticated } from '../api/prismAPI';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated()) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  // Helper function - determine default route for role
  const getDefaultRouteForRole = (role) => {
    if (role === 'care_coordinator' || role === 'org_clinical') return '/dashboard';
    if (role === 'team_lead') return '/coaching';
    if (role === 'leadership') return '/calls';
    if (role === 'platform_admin') return '/protocols';
    if (role === 'org_admin') return '/saas';
    return '/dashboard'; // fallback
  };

  const handleQuickLogin = async (demoUsername, demoPassword) => {
    setUsername(demoUsername);
    setPassword(demoPassword);
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: demoUsername, password: demoPassword }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Login failed");
      }

      const data = await response.json();
      
      localStorage.setItem("prism_token", data.access_token);
      localStorage.setItem("prism_username", data.username);
      
      const role = data.role || 'care_coordinator';
      const defaultRoute = getDefaultRouteForRole(role);
      const from = location.state?.from?.pathname || defaultRoute;
      
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Failed to login. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Login failed");
      }

      const data = await response.json();
      
      // Store token and username
      localStorage.setItem("prism_token", data.access_token);
      localStorage.setItem("prism_username", data.username);
      
      // Determine where to redirect based on role
      const role = data.role || 'care_coordinator';
      const defaultRoute = getDefaultRouteForRole(role);
      
      // If user was trying to access a specific page, go there. Otherwise, go to default.
      const from = location.state?.from?.pathname || defaultRoute;
      
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Failed to login. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, rgba(12, 20, 49, 1) 0%, rgba(40, 13, 58, 1) 50%, rgba(16, 61, 62, 1) 100%)",
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "90%",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(10px)",
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
            <Box
              component="img"
              src="/logo192.png"
              alt="Project Prism"
              sx={{
                width: 48,
                height: 48,
                mb: 1,
              }}
            />
            <Typography
              variant="h5"
              component="h1"
              sx={{ color: "white", fontWeight: 600, mb: 0.5 }}
            >
              Project Prism
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 2 }}
            >
              AI-Powered Protocol Discovery
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoFocus
              required
            />
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{ mt: 1, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
          </Box>

          {/* Quick Demo Login */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, textAlign: "center" }}>
              Quick Demo Login:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonIcon />}
                onClick={() => handleQuickLogin('coordinator', 'coord2025')}
                disabled={loading}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.9)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)"
                  }
                }}
              >
                Coordinator
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SupervisorAccountIcon />}
                onClick={() => handleQuickLogin('teamlead', 'lead2025')}
                disabled={loading}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.9)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)"
                  }
                }}
              >
                Team Lead
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AdminPanelSettingsIcon />}
                onClick={() => handleQuickLogin('jenn', 'prism2025')}
                disabled={loading}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.9)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    backgroundColor: "rgba(255, 255, 255, 0.05)"
                  }
                }}
              >
                Admin
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
